import Card from '@/components/Card';
import PieArcChart from '@/components/PieArcChart';
import { formatBytes } from '@/utils/formatters';
import { useTheme } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { HardDrive } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartContainer, ModelNameText } from './styles';
import { CardTitle } from '../styles';

interface DiskStatsProps {
  disks?: {
    modelName: string;
    total: number;
    free: number;
  }[];
}

/** Displays Server Disks it's storage space and model name */
function DiskStats({ disks }: DiskStatsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [currentDisk, setCurrentDisk] = useState('0');

  if (!disks) return null;

  // Calculates  and converts current selected disk storage metrics (total, free, used, percent)
  const diskMetrics = disks?.[Number(currentDisk)];
  const totalMemoryDisk = diskMetrics?.total || 1;
  const freeMemoryDisk = diskMetrics?.free || 0;
  const memoryDiskUsed = totalMemoryDisk - freeMemoryDisk;
  const memoryUsagePercent = Math.round(
    (memoryDiskUsed / totalMemoryDisk) * 100
  );
  const memoryUnit = formatBytes(totalMemoryDisk).split(' ')[1];

  // Disk storage data to be displayed on chart
  const diskPieChartData = [
    {
      value: Number(formatBytes(memoryDiskUsed, { base: 1024 }).split(' ')[0]),
      color: 'url(#pieChartGradient)',
      label: t('equipment.used'),
    },
    {
      value: Number(
        formatBytes(totalMemoryDisk - memoryDiskUsed, { base: 1024 }).split(
          ' '
        )[0]
      ),
      color: theme.palette.grey['05'],
      label: t('equipment.free'),
    },
  ];

  // handles a disk change when user select a disk
  const handleDiskChange = (
    _: React.MouseEvent<HTMLElement>,
    newCurrentDisk: string
  ) => {
    setCurrentDisk(newCurrentDisk);
  };

  return (
    <Card>
      <CardTitle>
        <HardDrive size="18px" />
        <Typography variant="h4">{t('dashboard.disk')}</Typography>

        {/** Select Current Disk */}
        <ToggleButtonGroup
          value={currentDisk}
          exclusive
          aria-label="text alignment"
          style={{ height: '24px' }}
          onChange={handleDiskChange}
          color="primary"
        >
          {disks.map((_, index) => (
            <ToggleButton value={index.toString()} aria-label="left aligned">
              {index + 1}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </CardTitle>

      {/** Pie Arc Chart */}
      <ChartContainer>
        <PieArcChart
          data={diskPieChartData || []}
          percentage={memoryUsagePercent || 0}
          unit={memoryUnit}
          legendPosition="bottom"
        />
      </ChartContainer>

      {/** Name */}
      <Stack direction="row" alignItems="center" gap="4px">
        <HardDrive size="14px" />
        <Typography variant="caption">{t('dashboard.model')} </Typography>
        <ModelNameText variant="caption">
          {disks[Number(currentDisk)].modelName}
        </ModelNameText>
      </Stack>
    </Card>
  );
}

export default DiskStats;
